import React, { useCallback, useState } from 'react'
import classnames from 'classnames'
import { type ScopeItem } from '../utils/scopeMetadata'

type AccessType = 'read' | 'write' | 'admin'

const ACCESS_TYPE_LABELS: Record<AccessType, string> = {
  read: 'Read',
  write: 'Write',
  admin: 'Admin',
}

const ACCESS_TYPE_VARIANTS: Record<AccessType, string> = {
  read: 'badge-access-read',
  write: 'bg-warning-subtle text-warning-emphasis',
  admin: 'bg-danger-subtle text-danger-emphasis',
}

interface OAuthScopeListProps {
  scopeGroups: Map<string, ScopeItem[]>
  selectedScopes: Set<string>
  onScopeToggle: (scope: string) => void
}

interface AccessBadgeProps {
  accessType: AccessType
}

const AccessBadge: React.FC<AccessBadgeProps> = ({ accessType }) => {
  const label = ACCESS_TYPE_LABELS[accessType]
  const variant = ACCESS_TYPE_VARIANTS[accessType]

  return <span className={classnames('badge rounded-3 fw-medium', variant)}>{label}</span>
}

interface ScopeRowProps {
  item: ScopeItem
  checked: boolean
  onToggle: (scope: string) => void
}

const ScopeRow: React.FC<ScopeRowProps> = ({ item, checked, onToggle }) => {
  const [expanded, setExpanded] = useState<boolean>(false)

  const toggle = useCallback(() => {
    setExpanded((prev) => !prev)
  }, [])

  const handleCheckboxChange = useCallback(() => {
    onToggle(item.scope)
  }, [onToggle, item.scope])

  const detailsId = `scope-details-${item.scope.replace(/[^a-zA-Z0-9]/g, '-')}`

  return (
    <div className='oauth-scope-item'>
      <div className='d-flex align-items-center gap-2'>
        <input
          type='checkbox'
          checked={checked}
          onChange={handleCheckboxChange}
          aria-label={`Grant ${item.label} access`}
          className='form-check-input flex-shrink-0 m-0'
        />
        <button
          type='button'
          className='btn btn-link text-decoration-none p-0 d-flex align-items-center gap-2 flex-grow-1 text-start'
          onClick={toggle}
          aria-expanded={expanded}
          aria-controls={detailsId}
        >
          <span className='text-body small fw-medium'>{item.label}</span>
          <i className={classnames('bi', expanded ? 'bi-chevron-down' : 'bi-chevron-right', 'text-muted small')} />
          <span className='flex-grow-1' />
          <AccessBadge accessType={item.accessType} />
        </button>
      </div>
      {expanded && (
        <div id={detailsId} className='ps-4 mt-1 mb-2 text-muted small'>
          {item.scope}
        </div>
      )}
    </div>
  )
}

export const OAuthScopeList: React.FC<OAuthScopeListProps> = ({ scopeGroups, selectedScopes, onScopeToggle }) => {
  const entries = Array.from(scopeGroups.entries())

  return (
    <div className='mb-4'>
      {entries.map(([category, items], index) => (
        <div key={category} className={classnames('mb-3', { 'border-bottom pb-3': index < entries.length - 1 })}>
          <div className='oauth-scope-category text-uppercase fw-bold text-body small mb-2'>{category}</div>
          {items.map((item) => (
            <ScopeRow key={item.scope} item={item} checked={selectedScopes.has(item.scope)} onToggle={onScopeToggle} />
          ))}
        </div>
      ))}
    </div>
  )
}
